import React, {useMemo, useState} from 'react';
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormHelperText,
    IconButton,
    InputAdornment,
    InputLabel,
    ListSubheader,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

export default function ServiceSelector({
                                            value = [],
                                            onChange,
                                            specializations = [],
                                            label = 'Услуги',
                                            error,
                                            helperText,
                                            required = false,
                                        }) {
    const [searchText, setSearchText] = useState('');
    const [menuOpen, setMenuOpen] = useState(false);
    const [showOnlySelected, setShowOnlySelected] = useState(false);

    // Фильтрация услуг по поиску и выбору
    const filteredSpecializations = useMemo(() => {
        let filtered = specializations;

        // Фильтр по поиску
        if (searchText.trim()) {
            const searchLower = searchText.toLowerCase();
            filtered = filtered
                .map(spec => ({
                    ...spec,
                    services: (spec.services || []).filter(service =>
                        service.title.toLowerCase().includes(searchLower)
                    ),
                }))
                .filter(spec => spec.services.length > 0);
        }

        // Фильтр "Только выбранные"
        if (showOnlySelected && value.length > 0) {
            filtered = filtered
                .map(spec => ({
                    ...spec,
                    services: (spec.services || []).filter(service =>
                        value.includes(service.id)
                    ),
                }))
                .filter(spec => spec.services.length > 0);
        }

        return filtered;
    }, [specializations, searchText, showOnlySelected, value]);

    // Все ID услуг (для "Выбрать все")
    const allServiceIds = useMemo(() => {
        return specializations.flatMap(spec =>
            (spec.services || []).map(s => s.id)
        );
    }, [specializations]);

    // Выбранные услуги (для отображения)
    const selectedServices = useMemo(() => {
        return specializations.flatMap(spec =>
            (spec.services || []).filter(s => value.includes(s.id))
        );
    }, [specializations, value]);

    const handleSelectAll = () => {
        onChange(allServiceIds);
    };

    const handleClearAll = () => {
        onChange([]);
    };

    const handleToggleService = (serviceId) => {
        const newValue = value.includes(serviceId)
            ? value.filter(id => id !== serviceId)
            : [...value, serviceId];
        onChange(newValue);
    };

    const handleClose = () => {
        setMenuOpen(false);
        setSearchText('');
        setShowOnlySelected(false);
    };

    return (
        <FormControl fullWidth margin="normal" required={required} error={error}>
            <InputLabel id={`${label}-label`}>{label}</InputLabel>
            <Select
                labelId={`${label}-label`}
                multiple
                open={menuOpen}
                onOpen={() => setMenuOpen(true)}
                onClose={handleClose}
                value={value}
                label={label}
                renderValue={(selected) => {
                    if (selected.length === 0) return 'Не выбрано';
                    if (selected.length === allServiceIds.length) return 'Все услуги выбраны';
                    return `Выбрано: ${selected.length}`;
                }}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            maxHeight: 600,
                            maxWidth: 600,
                            width: '100%',
                        },
                    },
                    autoFocus: false,
                    disablePortal: false,
                }}
            >
                {/* Заголовок с поиском и кнопками */}
                <ListSubheader
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        bgcolor: 'background.paper',
                        pt: 1,
                        pb: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                        <TextField
                            size="small"
                            placeholder="Поиск услуги..."
                            fullWidth
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small"/>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClose();
                            }}
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            <CloseIcon fontSize="small"/>
                        </IconButton>
                    </Box>
                    <Box sx={{display: 'flex', gap: 1, mb: 1}}>
                        <Button
                            size="small"
                            variant="outlined"
                            fullWidth
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSelectAll();
                            }}
                        >
                            Выбрать все
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            fullWidth
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClearAll();
                            }}
                        >
                            Очистить
                        </Button>
                    </Box>
                    <Box sx={{display: 'flex', alignItems: 'center'}}>
                        <Checkbox
                            size="small"
                            checked={showOnlySelected}
                            onChange={(e) => {
                                e.stopPropagation();
                                setShowOnlySelected(e.target.checked);
                            }}
                            disabled={value.length === 0}
                        />
                        <Typography
                            variant="body2"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (value.length > 0) {
                                    setShowOnlySelected(!showOnlySelected);
                                }
                            }}
                            sx={{
                                cursor: value.length > 0 ? 'pointer' : 'default',
                                opacity: value.length === 0 ? 0.5 : 1,
                                userSelect: 'none',
                            }}
                        >
                            Только выбранные
                        </Typography>
                    </Box>
                </ListSubheader>

                {/* Список услуг */}
                {filteredSpecializations.length === 0 ? (
                    <MenuItem disabled>
                        <Typography variant="body2" color="text.secondary">
                            Ничего не найдено
                        </Typography>
                    </MenuItem>
                ) : (
                    filteredSpecializations.flatMap(spec => [
                        <MenuItem
                            key={`spec-${spec.id}`}
                            disabled
                            sx={{
                                fontWeight: 'bold',
                                opacity: '1 !important',
                                bgcolor: 'action.hover',
                                cursor: 'default',
                            }}
                        >
                            {spec.name}
                        </MenuItem>,
                        ...(spec.services || []).map(service => {
                            const isChecked = value.indexOf(service.id) > -1;
                            return (
                                <MenuItem
                                    key={service.id}
                                    value={service.id}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleService(service.id);
                                    }}
                                    sx={{
                                        pl: 4,
                                        whiteSpace: 'normal',
                                        wordWrap: 'break-word',
                                    }}
                                >
                                    <Checkbox
                                        checked={isChecked}
                                        sx={{mr: 1}}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleToggleService(service.id);
                                        }}
                                    />
                                    <Typography variant="body2" sx={{wordBreak: 'break-word'}}>
                                        {service.title}
                                    </Typography>
                                </MenuItem>
                            );
                        })
                    ])
                )}
            </Select>
            {error && helperText && (
                <FormHelperText error>{helperText}</FormHelperText>
            )}
            {!error && helperText && (
                <FormHelperText>{helperText}</FormHelperText>
            )}
        </FormControl>
    );
}
